import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import { Phone, PhoneOff, Video } from "lucide-react";
import { playRingtone, type Ringtone } from "@athavita/ui-theme";
import { connectParityApi, normalizeUserId } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { connectHub } from "@/api/useConnectHub";
import { setHubAccessToken } from "@/api/sharedHub";

interface IncomingState {
    meetingId: string;
    subject: string;
    hostDisplayName: string;
    kind: "audio" | "video";
}

interface RingPayload {
    meetingId: string;
    subject?: string;
    hostDisplayName?: string;
    hostUserId?: string;
    /** Set by the server on every ring payload: true only for a call. */
    isCall?: boolean;
    /** Older name for the same thing (Mail's chat dock still reads it). */
    isCallNow?: boolean;
    kind?: "audio" | "video";
}

/**
 * Teams-style full-screen incoming call. Rings for calls only — it used to
 * ring for every meeting invitation, including meetings next week, because the
 * check fell through on any non-empty subject.
 *
 * Accepting opens the call at /meeting/:id (so a refresh keeps you in it)
 * instead of mounting the room inline; a video call can be answered with or
 * without video. Declining tells the caller at once, and answering or
 * declining on one tab stops the ring on the others.
 */
export function IncomingCallOverlay() {
    const token = useAccessToken();
    const auth = useAuth();
    const navigate = useNavigate();
    const myUserId = normalizeUserId(auth.user?.profile.sub);
    const [incoming, setIncoming] = useState<IncomingState | null>(null);
    const ringRef = useRef<Ringtone | null>(null);

    const stopRing = useCallback(() => {
        ringRef.current?.stop();
        ringRef.current = null;
    }, []);

    const clearIf = useCallback((meetingId: string) => {
        setIncoming((cur) => {
            if (cur && cur.meetingId === meetingId) {
                stopRing();
                return null;
            }
            return cur;
        });
    }, [stopRing]);

    // Rides the shell's shared ConnectHub connection. It used to open a second /hubs/connect socket of its
    // own (rebuilt on every token change and every shell remount), doubling each user's connections.
    useEffect(() => { setHubAccessToken(token); }, [token]);
    useEffect(() => {
        connectHub.acquire();
        const handle = (payload: RingPayload) => {
                if (payload.hostUserId && normalizeUserId(payload.hostUserId) === myUserId) return;
                const isCall = payload.isCall ?? payload.isCallNow ?? false;
                if (!isCall) return;
                setIncoming((prev) => prev ?? {
                    meetingId: payload.meetingId,
                    subject: payload.subject ?? "Incoming call",
                    hostDisplayName: payload.hostDisplayName ?? "Someone",
                    kind: payload.kind === "audio" ? "audio" : "video"
                });
                if (!ringRef.current) {
                    try { ringRef.current = playRingtone(); } catch { /* autoplay blocked */ }
                }
            };
        const offs = [
            connectHub.on("meeting.invited", (p) => handle(p as RingPayload)),
            connectHub.on("meeting.started", (p) => handle(p as RingPayload)),
            // Caller hung up before we answered.
            connectHub.on("meeting.ended", (p) => clearIf((p as { meetingId: string }).meetingId)),
            // Answered or declined on another tab or device.
            connectHub.on("meeting.call.stop-ringing", (p) => clearIf((p as { meetingId: string }).meetingId))
        ];
        return () => {
            for (const off of offs) off();
            connectHub.release();
            stopRing();
        };
    }, [myUserId, clearIf, stopRing]);

    const dismiss = useCallback(() => {
        setIncoming(null);
        stopRing();
    }, [stopRing]);

    // Stop ringing after 45 s — the caller's side shows "No answer" at the same time.
    useEffect(() => {
        if (!incoming) return;
        const id = window.setTimeout(() => dismiss(), 45_000);
        return () => window.clearTimeout(id);
    }, [incoming, dismiss]);

    const accept = useCallback((withVideo: boolean) => {
        if (!incoming) return;
        const id = incoming.meetingId;
        dismiss();
        navigate(`/meeting/${id}?answer=${withVideo ? "video" : "audio"}`);
    }, [incoming, dismiss, navigate]);

    const decline = useCallback(() => {
        if (!incoming) return;
        void connectParityApi.declineCall(incoming.meetingId, token).catch(() => undefined);
        dismiss();
    }, [incoming, token, dismiss]);

    if (!incoming) return null;
    const isVideo = incoming.kind === "video";

    return (
        <div
            role="alertdialog"
            aria-label={`Incoming ${isVideo ? "video" : "audio"} call from ${incoming.hostDisplayName}`}
            aria-live="assertive"
            className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/95 text-white backdrop-blur-sm"
        >
            <div className="flex flex-col items-center gap-6 p-8">
                <div className="relative">
                    <div className="absolute inset-0 animate-ping rounded-full bg-emerald-500/30" />
                    <div className="absolute inset-0 animate-ping rounded-full bg-emerald-500/20" style={{ animationDelay: "0.5s" }} />
                    <div className="relative flex h-32 w-32 items-center justify-center rounded-full bg-emerald-500/20 text-4xl font-bold">
                        {initials(incoming.hostDisplayName)}
                    </div>
                </div>
                <div className="text-center">
                    <div className="text-xs font-semibold uppercase tracking-widest text-white/60">
                        Incoming {isVideo ? "video" : "audio"} call
                    </div>
                    <div className="mt-2 text-2xl font-semibold">{incoming.hostDisplayName}</div>
                    <div className="mt-1 max-w-sm truncate text-sm text-white/70">{incoming.subject}</div>
                </div>
                <div className="flex items-end gap-8">
                    <CallAction label="Decline" onClick={decline} className="bg-rose-600 hover:bg-rose-500">
                        <PhoneOff className="h-7 w-7" />
                    </CallAction>
                    {isVideo ? (
                        <CallAction label="Audio only" onClick={() => accept(false)} className="bg-emerald-700 hover:bg-emerald-600">
                            <Phone className="h-7 w-7" />
                        </CallAction>
                    ) : null}
                    <CallAction
                        label={isVideo ? "Accept with video" : "Accept"}
                        onClick={() => accept(isVideo)}
                        className="bg-emerald-600 hover:bg-emerald-500"
                        autoFocus
                    >
                        {isVideo ? <Video className="h-7 w-7" /> : <Phone className="h-7 w-7" />}
                    </CallAction>
                </div>
            </div>
        </div>
    );
}

function CallAction({ label, onClick, className, autoFocus, children }: {
    label: string;
    onClick: () => void;
    className: string;
    autoFocus?: boolean;
    children: React.ReactNode;
}) {
    return (
        <div className="flex flex-col items-center gap-2">
            <button
                type="button"
                onClick={onClick}
                aria-label={label}
                title={label}
                autoFocus={autoFocus}
                className={`flex h-16 w-16 items-center justify-center rounded-full text-white focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/60 ${className}`}
            >
                {children}
            </button>
            <span className="text-xs text-white/70">{label}</span>
        </div>
    );
}

function initials(name: string): string {
    const words = name.split(/\s+/).filter(Boolean);
    if (words.length === 0) return "?";
    if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
    return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
}
